import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Invoice } from '../../types';
import Input from './Input';
import Select from './Select';
import TextArea from './TextArea';

interface SelectOption {
  value: string;
  label: string;
}

interface InvoiceFormFieldsProps {
  register: UseFormRegister<Invoice>;
  errors: FieldErrors<Invoice>;
  isDebitNote: boolean;
  invoiceTypeOptions: SelectOption[];
  clientOptions: SelectOption[];
  statusOptions: SelectOption[];
  branchOptions: SelectOption[];
  foaOptions: SelectOption[];
  selectedFoaId: string | undefined;
  foas: any[];
}

const InvoiceFormFields: React.FC<InvoiceFormFieldsProps> = ({
  register,
  errors,
  isDebitNote,
  invoiceTypeOptions,
  clientOptions,
  statusOptions,
  branchOptions,
  foaOptions,
  selectedFoaId,
  foas,
}) => {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Select label="Invoice Type" {...register('invoiceType')} options={invoiceTypeOptions} />
        <Select label="Client" {...register('clientId', { required: 'Client is required' })} options={clientOptions} error={errors.clientId?.message} />
        <Select label="Status" {...register('status', { required: 'Status is required' })} options={statusOptions} error={errors.status?.message} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Input label="Issue Date" type="date" {...register('issueDate', { required: 'Issue date is required' })} error={errors.issueDate?.message} />
        <Input label="Due Date" type="date" {...register('dueDate', { required: 'Due date is required' })} error={errors.dueDate?.message} />
        <Input label="Operation Date" type="date" {...register('operationDate', { required: 'Operation date is required' })} error={errors.operationDate?.message} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select label="Vessel / Headquarters" {...register('vesselId', { required: 'Vessel/Headquarters is required' })} options={branchOptions} error={errors.vesselId?.message} />
        <Select label="FOA (امر الاسناد)" {...register('foaId')} options={foaOptions} error={errors.foaId?.message} />
      </div>

      {selectedFoaId && foas.length > 0 && (() => {
        const selectedFoa = foas.find((f: any) => (f.documentId || f.id) === selectedFoaId);
        if (!selectedFoa) return null;
        return (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>FOA Reference:</strong> {selectedFoa.foaNumber} - {selectedFoa.englishName} |
              Amount: {selectedFoa.currency1 ?? selectedFoa.currency ?? ''} {Number(selectedFoa.amount1 ?? selectedFoa.amount ?? 0).toLocaleString()}
              {' '}(auto-filled to subtotal, editable)
            </p>
          </div>
        );
      })()}

      <TextArea label="Description" {...register('description', { required: 'Description is required' })} error={errors.description?.message} placeholder="Enter service description" rows={2} />

      {isDebitNote && (
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Debit Note Content (Printable)</h3>
          <textarea
            {...register('freeTextContent')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            rows={10}
            placeholder="Enter debit note content here. This text will be printed on the debit note..."
          />
        </div>
      )}
    </>
  );
};

export default InvoiceFormFields;
